// Phase-0 visual smoke test for primitives, icons, tokens, and mock data.
// Lives at /dev/showcase. Not linked from app navigation.
import { useState } from "react";
import { Link } from "react-router-dom";
import { Icon } from "@/components/Icon/Icon";
import {
  AutoTextarea,
  Brandmark,
  Button,
  Chip,
  Divider,
  Input,
  Kbd,
  Label,
  Logo,
  Pill,
  Segmented,
  SlashLabel,
  Switch,
  Textarea,
  VerticalDivider,
} from "@/components/primitives";
import type { IconName } from "@/utils/types/ui";
import { FLAVOR_FAMILIES, RECOMMENDATIONS, COMPOUND_LIB } from "@/services/mock";

const ICONS: IconName[] = [
  "plus", "search", "upload", "sparkle", "text", "flask", "chevron",
  "chevron-r", "x", "settings", "star", "star-fill", "filter", "dots",
  "trash", "download", "share", "check", "copy", "reset", "lock",
  "arrow-r", "arrow-l", "bookmark", "menu", "info", "zap", "molecule",
  "beaker", "target", "compass", "wand", "atom", "history", "folder",
  "file", "box", "truck", "shield", "alert", "external", "edit", "panel-l",
];

export function DevShowcase() {
  const [seg, setSeg] = useState<"design" | "make" | "assess">("design");
  const [sw, setSw] = useState(false);
  const [auto, setAuto] = useState("");

  return (
    <div
      style={{
        padding: "32px 40px",
        maxWidth: 1100,
        margin: "0 auto",
        background: "var(--bg)",
        minHeight: "100vh",
      }}
    >
      <div className="hstack" style={{ justifyContent: "space-between", marginBottom: 24 }}>
        <Brandmark />
        <SlashLabel>Phase 0 — Foundation showcase</SlashLabel>
      </div>

      <Section title="Logo + Brandmark">
        <div className="hstack gap-4">
          <Logo size={22} />
          <Logo size={28} />
          <Logo size={48} />
          <VerticalDivider />
          <Brandmark />
        </div>
      </Section>

      <Section title="Buttons">
        <div className="hstack gap-3">
          <Button>Default</Button>
          <Button variant="primary">Primary</Button>
          <Button variant="ghost">Ghost</Button>
          <Button variant="accent-outline">Accent outline</Button>
          <Button size="sm">Small</Button>
          <Button size="xs">Extra small</Button>
          <Button disabled>Disabled</Button>
        </div>
      </Section>

      <Section title="Inputs">
        <div className="vstack gap-3" style={{ maxWidth: 440 }}>
          <div className="vstack gap-1">
            <Label>Email</Label>
            <Input placeholder="you@company.com" />
          </div>
          <div className="vstack gap-1">
            <Label>Password</Label>
            <Input type="password" placeholder="••••••••" />
          </div>
          <div className="vstack gap-1">
            <Label>Notes</Label>
            <Textarea placeholder="Plain textarea" rows={3} />
          </div>
          <div className="vstack gap-1">
            <Label>Auto-growing textarea</Label>
            <AutoTextarea
              value={auto}
              onChange={setAuto}
              placeholder="Type to grow…"
            />
          </div>
        </div>
      </Section>

      <Section title="Switch & Segmented">
        <div className="hstack gap-4">
          <div className="hstack gap-2">
            <Label>Compact</Label>
            <Switch checked={sw} onChange={setSw} />
          </div>
          <Segmented
            value={seg}
            onChange={setSeg}
            options={[
              { value: "design", label: "Design" },
              { value: "make", label: "Make" },
              { value: "assess", label: "Assess" },
            ]}
          />
        </div>
      </Section>

      <Section title="Chips, kbd, pills, slash-label">
        <div className="hstack gap-3">
          <Chip>Default chip</Chip>
          <Chip variant="accent">Accent</Chip>
          <Chip variant="signal">Signal</Chip>
          <VerticalDivider />
          <Kbd>⌘N</Kbd>
          <Kbd>⏎</Kbd>
          <VerticalDivider />
          {Object.keys(FLAVOR_FAMILIES).map((fam) => (
            <Pill key={fam} family={fam} />
          ))}
        </div>
        <div style={{ marginTop: 12 }}>
          <SlashLabel>AI generating right now</SlashLabel>
        </div>
      </Section>

      <Section title="Icon catalogue">
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(8, 1fr)",
            gap: 12,
          }}
        >
          {ICONS.map((name) => (
            <div
              key={name}
              className="panel"
              style={{
                padding: 12,
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                gap: 6,
                color: "var(--ink-2)",
              }}
            >
              <Icon name={name} size={20} />
              <span className="mono" style={{ fontSize: 10, color: "var(--ink-4)" }}>
                {name}
              </span>
            </div>
          ))}
        </div>
      </Section>

      <Section title="Pages">
        <div className="hstack gap-3" style={{ flexWrap: "wrap" }}>
          <Link to="/sign-in">
            <Button variant="primary">/sign-in</Button>
          </Link>
          <Link to="/sign-in/sso-callback">
            <Button variant="ghost">/sign-in/sso-callback</Button>
          </Link>
          <Link to="/">
            <Button variant="ghost">/</Button>
          </Link>
          <Link to="/flavors/new">
            <Button variant="ghost">/flavors/new</Button>
          </Link>
          <Link to="/flavors/new/generating">
            <Button variant="ghost">/flavors/new/generating</Button>
          </Link>
          <Link to="/flavors/w1">
            <Button variant="ghost">/flavors/w1</Button>
          </Link>
          <Link to="/flavors/w1?phase=make">
            <Button variant="ghost">/flavors/w1?phase=make</Button>
          </Link>
          <Link to="/flavors/w1?phase=assess">
            <Button variant="ghost">/flavors/w1?phase=assess</Button>
          </Link>
          <Link to="/dev/plots">
            <Button variant="ghost">/dev/plots</Button>
          </Link>
        </div>
        <div style={{ marginTop: 8, fontSize: 11, color: "var(--ink-4)" }}>
          Sign-in uses <code>useSignIn()</code>; SSO finalizes via
          <code> useClerk().handleRedirectCallback()</code>. The protected shell
          (sidebar + Settings popover) wraps <code>/</code>, the wizard, the
          generating scene, and <code>/flavors/:id</code>. Wizard submits to
          either the workbench (catalog/discover) or the generating scene
          (chromatogram).
        </div>
      </Section>

      <Section title="Mock services smoke">
        <div className="hstack gap-4" style={{ alignItems: "flex-start" }}>
          <div className="panel" style={{ padding: 14, flex: 1 }}>
            <Label>Recommendations ({RECOMMENDATIONS.length})</Label>
            <ul className="mono" style={{ fontSize: 11, marginTop: 8, paddingLeft: 16 }}>
              {RECOMMENDATIONS.slice(0, 4).map((r) => (
                <li key={r.id}>{r.name}</li>
              ))}
            </ul>
          </div>
          <div className="panel" style={{ padding: 14, flex: 1 }}>
            <Label>Compound library ({COMPOUND_LIB.length})</Label>
            <ul className="mono" style={{ fontSize: 11, marginTop: 8, paddingLeft: 16 }}>
              {COMPOUND_LIB.slice(0, 4).map((c) => (
                <li key={c.id}>{c.name}</li>
              ))}
            </ul>
          </div>
        </div>
      </Section>
    </div>
  );
}

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <section style={{ marginTop: 28 }}>
      <h2
        className="display"
        style={{ fontSize: 14, marginBottom: 12, color: "var(--ink-2)" }}
      >
        {title}
      </h2>
      {children}
      <div style={{ marginTop: 20 }}>
        <Divider />
      </div>
    </section>
  );
}
